import { Brands, Experiments, PaymentCycle } from 'enums';
import {
  AdvancedPlansProductData,
  BaseProduct,
  StringMap,
} from '../../../types';
import { getProductFamilyIsInSale } from '../../../utils';

export type SortFunction<T> = (p1: T, p2: T) => number;

export interface PlanSorterProps {
  isMobile: boolean;
  isInSale: boolean;
  experiments: StringMap;
  brand: Brands;
}

enum SortType {
  AscendingByOrder,
  DescendingByOrder,
  SaleFirstAscendingByOrder,
  AscendingByPrice,
  DescendingByPrice,
  KeepOrder,
}

const ascending: SortFunction<number> = (a, b) => a - b;
export const descending: SortFunction<number> = (a, b) => b - a;

export const ascendingPlansSort: SortFunction<BaseProduct> = (prod1, prod2) =>
  ascending(prod1.order, prod2.order);

export const descendingPlansSort: SortFunction<BaseProduct> = (prod1, prod2) =>
  descending(prod1.order, prod2.order);

export const saleFirstAscending: SortFunction<BaseProduct> = (
  prod1: BaseProduct,
  prod2: BaseProduct,
) => {
  const prod1IsInSale = getProductFamilyIsInSale(prod1.productFamily);
  const prod2IsInSale = getProductFamilyIsInSale(prod2.productFamily);
  if (prod1IsInSale === prod2IsInSale) {
    return ascendingPlansSort(prod1, prod2);
  }
  return prod1IsInSale ? -1 : 1;
};

export const sortByPrice = (
  prod1: BaseProduct,
  prod2: BaseProduct,
  orderFunction: SortFunction<number>,
) => {
  const prod1Price =
    prod1.cycles.find((c) => c.cycleId === PaymentCycle.YEARLY)
      ?.fullTermActualPrice ?? 0;
  const prod2Price =
    prod2.cycles.find((c) => c.cycleId === PaymentCycle.YEARLY)
      ?.fullTermActualPrice ?? 0;

  return orderFunction(prod1Price, prod2Price);
};

export const ascendingByPrice: SortFunction<BaseProduct> = (p1, p2) =>
  sortByPrice(p1, p2, ascending);
export const descendingByPrice: SortFunction<BaseProduct> = (p1, p2) =>
  sortByPrice(p1, p2, descending);
export const keepOrder: SortFunction<BaseProduct> = () => 0;

const sortersMap: { [k in SortType]: SortFunction<BaseProduct> } = {
  [SortType.AscendingByOrder]: ascendingPlansSort,
  [SortType.DescendingByOrder]: descendingPlansSort,
  [SortType.SaleFirstAscendingByOrder]: saleFirstAscending,
  [SortType.AscendingByPrice]: ascendingByPrice,
  [SortType.DescendingByPrice]: descendingByPrice,
  [SortType.KeepOrder]: keepOrder,
};

export class PlanSorter {
  private sorter: (
    products: AdvancedPlansProductData[],
  ) => AdvancedPlansProductData[];

  constructor({ isMobile, isInSale, experiments, brand }: PlanSorterProps) {
    const salePlansFirst =
      isInSale && experiments[Experiments.MobileSalePlansFirst] === 'true';
    const dealerAscendingOrder =
      experiments[Experiments.DealerPlansOrder] === 'true' ||
      experiments[Experiments.ReversePlansOrder] === '1';

    let sortType = isMobile
      ? salePlansFirst
        ? SortType.SaleFirstAscendingByOrder
        : SortType.AscendingByOrder
      : dealerAscendingOrder
      ? SortType.AscendingByOrder
      : SortType.DescendingByOrder;

    if (brand === Brands.DAYFUL) {
      sortType = isMobile
        ? SortType.AscendingByPrice
        : SortType.DescendingByPrice;
    }

    this.sorter = (products) => products.sort(sortersMap[sortType]);
  }

  sort = (products: AdvancedPlansProductData[]) => this.sorter(products);
}
